import React, { useState } from 'react';
import { Card, Descriptions, Tag, Space, Typography, Divider, Collapse, Timeline, Table } from 'antd';
import { 
  UserOutlined, 
  PhoneOutlined, 
  MailOutlined, 
  CalendarOutlined, 
  HomeOutlined,
  DollarOutlined,
  ProjectOutlined,
  HistoryOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Panel } = Collapse;

export default function DynamicLeadDetails({ leadData }) {
  const [activeKey, setActiveKey] = useState(['basic', 'contact', 'project']);

  if (!leadData) {
    return <div>No lead data provided</div>;
  }

  // Helper function to format field names
  const formatFieldName = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Helper function to format values
  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (Array.isArray(value)) {
      if (value.length === 0) return 'N/A';
      return value.map(item => {
        if (typeof item === 'object') return JSON.stringify(item);
        return String(item);
      }).join(', ');
    }
    if (typeof value === 'object') {
      if (value.address_line) return value.address_line;
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'string' && value.includes('T') && value.includes('Z')) {
      try {
        return new Date(value).toLocaleString();
      } catch {
        return value;
      }
    }
    return String(value);
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    const statusLower = String(status).toLowerCase();
    if (statusLower.includes('open') || statusLower.includes('active')) return 'green';
    if (statusLower.includes('site_visit_done')) return 'blue';
    if (statusLower.includes('re_visit_done')) return 'orange';
    if (statusLower.includes('lost') || statusLower.includes('closed')) return 'red';
    return 'default';
  };

  // Extract basic information
  const basicInfo = [
    { label: 'Lead ID', value: leadData.lead_id, icon: <InfoCircleOutlined /> },
    { label: 'Name', value: leadData.name, icon: <UserOutlined /> },
    { label: 'State', value: leadData.state, icon: <Tag color={getStatusColor(leadData.state)}>{leadData.state}</Tag> },
    { label: 'Lead Creation Time', value: formatValue(leadData.lead_creation_time), icon: <CalendarOutlined /> },
    { label: 'Source Tags', value: formatValue(leadData.source_tags), icon: <ProjectOutlined /> },
    { label: 'Lead Group', value: leadData.lead_group, icon: <InfoCircleOutlined /> },
  ];

  // Extract contact information
  const contactInfo = [
    ...leadData.phones?.map((phone, index) => ({
      label: `Phone ${index + 1} (${phone.type})`,
      value: phone.phone,
      icon: <PhoneOutlined />
    })) || [],
    ...leadData.emails?.map((email, index) => ({
      label: `Email ${index + 1} (${email.type})`,
      value: email.email,
      icon: <MailOutlined />
    })) || []
  ];

  // Extract assigned user information
  const assignedUserInfo = leadData.assigned_to ? [
    { label: 'Assigned To', value: leadData.assigned_to.name, icon: <UserOutlined /> },
    { label: 'Assigned User Phone', value: leadData.assigned_to.user_phone, icon: <PhoneOutlined /> },
    { label: 'Tracking Number', value: leadData.assigned_to.tracking_number || 'N/A', icon: <InfoCircleOutlined /> },
    { label: 'User ID', value: leadData.assigned_to.id, icon: <InfoCircleOutlined /> },
  ] : [];

  // Extract project and business information from lead_fields
  const projectInfo = [];
  const businessInfo = [];
  const personalInfo = [];
  const otherInfo = [];

  if (leadData.lead_fields) {
    Object.entries(leadData.lead_fields).forEach(([key, value]) => {
      const item = { label: formatFieldName(key), value: formatValue(value), key };
      
      // Categorize fields
      if (key.includes('project') || key.includes('unit') || key.includes('location') || key.includes('address')) {
        projectInfo.push(item);
      } else if (key.includes('budget') || key.includes('company') || key.includes('work') || key.includes('buying')) {
        businessInfo.push(item);
      } else if (key.includes('language') || key.includes('city') || key.includes('state') || key.includes('pincode')) {
        personalInfo.push(item);
      } else {
        otherInfo.push(item);
      }
    });
  }

  // Create timeline data from transitions
  const createTimeline = (transitions) => {
    if (!transitions || !transitions.state) return [];
    
    return transitions.state.map((transition, index) => ({
      key: index,
      color: getStatusColor(transition.to),
      children: (
        <div>
          <Text strong>{formatFieldName(transition.to)}</Text>
          <br />
          <Text type="secondary">
            Duration: {Math.floor(transition.duration_secs / 3600)}h {Math.floor((transition.duration_secs % 3600) / 60)}m
          </Text>
          <br />
          <Text type="secondary">
            {transition.start_time ? new Date(transition.start_time * 1000).toLocaleString() : 'N/A'}
          </Text>
        </div>
      )
    }));
  };

  // Create user assignment history table
  const createAssignmentHistory = (transitions) => {
    if (!transitions || !transitions.user_assigned_to) return [];
    
    return transitions.user_assigned_to.map((transition, index) => ({
      key: index,
      from: transition.from || 'Initial',
      to: transition.to,
      duration: `${Math.floor(transition.duration_secs / 3600)}h ${Math.floor((transition.duration_secs % 3600) / 60)}m`,
      startTime: transition.start_time ? new Date(transition.start_time * 1000).toLocaleString() : 'N/A',
      endTime: transition.end_time ? new Date(transition.end_time * 1000).toLocaleString() : 'Current'
    }));
  };

  const assignmentColumns = [
    { title: 'From User', dataIndex: 'from', key: 'from' },
    { title: 'To User', dataIndex: 'to', key: 'to' },
    { title: 'Duration', dataIndex: 'duration', key: 'duration' },
    { title: 'Start Time', dataIndex: 'startTime', key: 'startTime' },
    { title: 'End Time', dataIndex: 'endTime', key: 'endTime' },
  ];

  const renderDescriptions = (items, column = 2) => (
    <Descriptions bordered column={column} size="small">
      {items.map((item, index) => (
        <Descriptions.Item 
          key={index} 
          label={
            <Space>
              {item.icon}
              <span>{item.label}</span>
            </Space>
          }
        >
          {typeof item.value === 'object' && React.isValidElement(item.value) 
            ? item.value 
            : <Text>{item.value}</Text>
          }
        </Descriptions.Item>
      ))}
    </Descriptions>
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Card>
        <Title level={3}>
          <UserOutlined /> Lead Details: {leadData.name}
        </Title>
        
        <Collapse 
          activeKey={activeKey} 
          onChange={setActiveKey}
          style={{ marginTop: 20 }}
        >
          {/* Basic Information */}
          <Panel header="Basic Information" key="basic">
            {renderDescriptions(basicInfo)}
          </Panel>

          {/* Contact Information */}
          {contactInfo.length > 0 && (
            <Panel header="Contact Information" key="contact">
              {renderDescriptions(contactInfo)}
            </Panel>
          )}

          {/* Assigned User Information */}
          {assignedUserInfo.length > 0 && (
            <Panel header="Assigned User" key="assigned">
              {renderDescriptions(assignedUserInfo)}
            </Panel>
          )}

          {/* Project Information */}
          {projectInfo.length > 0 && (
            <Panel header="Project & Property Details" key="project">
              {renderDescriptions(projectInfo, 1)}
            </Panel>
          )}

          {/* Business Information */}
          {businessInfo.length > 0 && (
            <Panel header="Business & Budget Information" key="business">
              {renderDescriptions(businessInfo, 1)}
            </Panel>
          )}

          {/* Personal Information */}
          {personalInfo.length > 0 && (
            <Panel header="Personal Information" key="personal">
              {renderDescriptions(personalInfo)}
            </Panel>
          )}

          {/* Other Custom Fields */}
          {otherInfo.length > 0 && (
            <Panel header="Other Information" key="other">
              {renderDescriptions(otherInfo, 1)}
            </Panel>
          )}

          {/* State Transition History */}
          {leadData.transitions?.state && (
            <Panel header="State History" key="history">
              <Timeline items={createTimeline(leadData.transitions)} />
            </Panel>
          )}

          {/* User Assignment History */}
          {leadData.transitions?.user_assigned_to && (
            <Panel header="Assignment History" key="assignments">
              <Table 
                dataSource={createAssignmentHistory(leadData.transitions)}
                columns={assignmentColumns}
                pagination={false}
                size="small"
              />
            </Panel>
          )}

          {/* Raw Data */}
          <Panel header="Raw Data (Debug)" key="raw">
            <pre style={{ 
              backgroundColor: '#f5f5f5', 
              padding: '15px', 
              borderRadius: '4px',
              fontSize: '12px',
              overflow: 'auto',
              maxHeight: '400px'
            }}>
              {JSON.stringify(leadData, null, 2)}
            </pre>
          </Panel>
        </Collapse>
      </Card>
    </div>
  );
}

// Example usage component
export function DynamicLeadDetailsExample() {
  const sampleLeadData = {
    "allow_unmerge": false,
    "lead_id": "6c40c8ce-cbe6-4f1c-aeaf-394fef37628d",
    "source_tags": ["Offline", "Site Visit Form"],
    "lead_creation_time": "2025-08-04T09:15:15Z",
    "phones": [{"phone": "+918840016234", "type": "Work", "shared": true}],
    "lead_group": "5636140f-2ecd-44da-838f-be860d516f31",
    "lead_fields": {
      "budget_interested__copy__c02": ["30L-45L"],
      "project_revisited4__copy__a15": ["41 CITY HUB"],
      "customer_id_97b": ["KD16823"],
      "buying_purpose_19a51b85": ["Investment"],
      "visit_date__copy__e5d": ["2025-08-22T00:00:00Z"],
      "unit_type_1__copy__36c": [["1bhk"]],
      "company_name_c66": ["MetroGuild"],
      "location__ac1e6456": [["Pune"]],
      "address_f6f61448": [{"address_line": "House Number- 55, Royal City, Bijnor Road"}],
      "state_9d4": ["Uttar Pradesh"],
      "city_0b6": [["Lucknow"]],
      "closing_manager_cb3": ["Ashish Ram Pratap Mishra"]
    },
    "state": "site_visit_done",
    "assigned_to": {
      "user_phone": "+917757942640",
      "name": "Inder Kumar",
      "tracking_number": "",
      "id": "b28b3ecd-eb4f-4b74-87c8-3e1a7d61a692"
    },
    "emails": [{"shared": true, "type": "Work", "email": "rahul.vasonomics@gmail.com"}],
    "name": "Test Rahul"
  };

  return <DynamicLeadDetails leadData={sampleLeadData} />;
}